import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users, MapPin, Search, Filter,
    MessageCircle, Shield, UserPlus, Radio, Camera
} from 'lucide-react';
import { Contact, SafetyStatus } from '../types';
import ContactDetailModal from './ContactDetailModal';
import AddContactFlow from './AddContactFlow';
import AvatarLibrary from './AvatarLibrary';
import { contactService } from '../services/contactService';

// --- THEME CONSTANTS ---
// Deep Starlight Blue Theme
const THEME = {
    bg: 'bg-[#02050e]',
    card: 'bg-[#0f172a]',
    accent: 'text-blue-400',
    accentBg: 'bg-blue-500/10',
    border: 'border-blue-500/20'
};

interface ContactPageProps {
    currentUser: Contact | null;
    contacts: Contact[];
    onAddContact: (id: string) => void;
    onShare: () => void;
    onLogout: () => void;
    onTrackContact: (id: string) => void;
    onOpenChat: (contact: Contact) => void;
    onOpenMessenger: () => void;
    onRemoveContact: (id: string) => void;
    onViewProfile: (id: string) => void;
}

const ContactPage: React.FC<ContactPageProps> = ({
    currentUser,
    contacts,
    onAddContact,
    onTrackContact,
    onOpenChat,
    onOpenMessenger,
    onRemoveContact,
    onViewProfile
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isAddFlowOpen, setIsAddFlowOpen] = useState(false);
    const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState(false);
    const [selectedDetailContact, setSelectedDetailContact] = useState<Contact | null>(null);

    // Filter contacts
    const filteredContacts = contacts.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Helper Status Color
    const getStatusColor = (status: SafetyStatus) => {
        switch (status) {
            case SafetyStatus.SAFE:
            case SafetyStatus.HOME:
                return 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10';
            case SafetyStatus.DANGER:
            case SafetyStatus.SOS:
                return 'text-red-500 border-red-500/30 bg-red-500/10 animate-pulse';
            case SafetyStatus.WARNING:
                return 'text-amber-400 border-amber-500/30 bg-amber-500/10';
            default:
                return 'text-slate-400 border-slate-500/30 bg-slate-500/10';
        }
    };

    return (
        <div className="h-full w-full relative overflow-hidden" style={{ backgroundColor: 'var(--bg-deepest)', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)' }}>
            {/* Nature-Tech Background Effects */}
            <div className="absolute inset-0 pointer-events-none opacity-15">
                <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full blur-[120px] animate-breathe" style={{ background: 'radial-gradient(circle, rgba(96,165,250,0.3) 0%, transparent 70%)' }} />
                <div className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full blur-[100px] animate-leaf-float" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.2) 0%, transparent 70%)' }} />
            </div>

            {/* Header - Touch Optimized */}
            <header className="sticky top-0 z-20 backdrop-blur-xl px-6 pt-6 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-black tracking-wide flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                            <Users size={24} style={{ color: 'var(--accent-primary)' }} />
                            NETWORK
                        </h1>
                        <p className="text-xs font-mono tracking-widest mt-1" style={{ color: 'var(--text-muted)' }}>
                            SQUAD STATUS // <span style={{ color: 'var(--accent-primary)' }}>{contacts.length} ACTIVE</span>
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={onOpenMessenger}
                            className="w-12 h-12 rounded-2xl backdrop-blur flex items-center justify-center transition-all active:scale-95"
                            style={{ backgroundColor: 'var(--bg-glass)', border: '1px solid rgba(255,255,255,0.1)' }}
                        >
                            <MessageCircle size={18} style={{ color: 'var(--text-primary)' }} />
                        </button>
                        <button
                            onClick={() => setIsAddFlowOpen(true)}
                            className="h-12 px-5 rounded-2xl text-xs font-bold tracking-wider uppercase transition-all shadow-lg flex items-center gap-2 active:scale-95"
                            style={{ backgroundColor: 'var(--accent-primary)', color: 'white', boxShadow: '0 0 20px rgba(96,165,250,0.2)' }}
                        >
                            <UserPlus size={16} /> Add Agent
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search operative name or ID..."
                        className="w-full pl-11 pr-4 py-3.5 rounded-2xl text-sm font-mono focus:outline-none transition-all"
                        style={{
                            backgroundColor: 'var(--bg-glass)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'var(--text-primary)'
                        }}
                    />
                </div>
            </header>

            {/* Main List */}
            <div className="px-6 pb-32 overflow-y-auto h-[calc(100%-160px)] custom-scrollbar">

                {/* My Card - Nature-Tech */}
                <div className="mt-4 mb-8 relative group">
                    <div className="absolute -inset-0.5 rounded-3xl opacity-20 group-hover:opacity-30 transition duration-500 blur animate-shimmer" style={{ background: 'linear-gradient(90deg, var(--accent-primary) 0%, var(--accent-safe) 100%)' }}></div>
                    <div className="relative p-5 rounded-3xl backdrop-blur flex items-center gap-4" style={{ backgroundColor: 'var(--bg-glass-strong)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div className="relative">
                            <div className="w-16 h-16 rounded-2xl overflow-hidden bg-black" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                                <img
                                    src={currentUser?.avatarUrl || 'https://i.pravatar.cc/150?u=me'}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <button
                                onClick={() => setIsAvatarPickerOpen(true)}
                                className="absolute -bottom-2 -right-2 p-2 rounded-xl text-white shadow-lg transition-all active:scale-95"
                                style={{ backgroundColor: 'var(--accent-primary)', border: '2px solid var(--bg-deepest)' }}
                            >
                                <Camera size={14} />
                            </button>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold tracking-wide" style={{ color: 'var(--text-primary)' }}>{currentUser?.name || 'Unknown Agent'}</h3>
                            <p className="text-xs font-mono mt-0.5 break-all" style={{ color: 'var(--text-muted)' }}>ID: {currentUser?.id}</p>
                            <div className="flex items-center gap-2 mt-2">
                                <span className={`text-xs font-bold px-2 py-1 rounded-xl border ${getStatusColor(currentUser?.status || SafetyStatus.SAFE)}`}>
                                    {currentUser?.status || 'SAFE'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contacts Grid - Nature-Tech */}
                {filteredContacts.length === 0 ? (
                    <div className="text-center py-12 opacity-50">
                        <Users className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                        <p className="text-xs font-mono  uppercase" style={{ color: 'var(--text-muted)' }}>No Matching Agents</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <h3 className="text-xs font-bold uppercase tracking-widest mb-4 pl-1" style={{ color: 'var(--text-muted)' }}>Squad Members</h3>
                        {filteredContacts.map(contact => (
                            <motion.div
                                key={contact.id}
                                layoutId={contact.id}
                                onClick={() => setSelectedDetailContact(contact)}
                                className="p-4 rounded-2xl backdrop-blur flex items-center gap-3 cursor-pointer group transition-all active:scale-98"
                                style={{ backgroundColor: 'var(--bg-glass)', border: '1px solid rgba(255,255,255,0.05)' }}
                                whileHover={{ scale: 1.01 }}
                            >
                                {/* Avatar */}
                                <div className="relative">
                                    <div className="w-14 h-14 rounded-xl bg-black/50 overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                                        <img src={contact.avatarUrl || `https://i.pravatar.cc/150?u=${contact.id}`} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 animate-soft-pulse" style={{
                                        borderColor: 'var(--bg-deepest)',
                                        backgroundColor: contact.status === SafetyStatus.SAFE ? 'var(--accent-safe)' : 'var(--accent-danger)'
                                    }} />
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-center mb-0.5">
                                        <h4 className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{contact.name}</h4>
                                        <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{contact.distance || '?? km'}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <MapPin size={10} style={{ color: 'var(--text-muted)' }} />
                                        <p className="text-xs truncate max-w-[140px]" style={{ color: 'var(--text-secondary)' }}>{contact.locationName || 'Unknown Sector'}</p>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onOpenChat(contact); }}
                                        className="p-2.5 rounded-xl transition-all active:scale-95"
                                        style={{ backgroundColor: 'rgba(96,165,250,0.2)', color: 'var(--accent-primary)' }}
                                    >
                                        <MessageCircle size={16} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            <AddContactFlow
                isOpen={isAddFlowOpen}
                onClose={() => setIsAddFlowOpen(false)}
                onAddContact={onAddContact}
                myId={currentUser?.id || ''}
                myName={currentUser?.name || ''}
            />

            <AvatarLibrary
                isOpen={isAvatarPickerOpen}
                onClose={() => setIsAvatarPickerOpen(false)}
                onSelect={(url) => {
                    contactService.updateProfileSettings(currentUser?.id || '', { avatar_url: url });
                    setIsAvatarPickerOpen(false);
                    // Force refresh or optimistic update usually handled by parent
                }}
                currentAvatar={currentUser?.avatarUrl}
            />

            {selectedDetailContact && (
                <ContactDetailModal
                    contact={selectedDetailContact}
                    onClose={() => setSelectedDetailContact(null)}
                    onChat={() => {
                        onOpenChat(selectedDetailContact);
                        setSelectedDetailContact(null);
                    }}
                    onTrack={() => {
                        onTrackContact(selectedDetailContact.id);
                        setSelectedDetailContact(null);
                    }}
                    onPulse={() => { /* Handled by pulse page usually, or add quick pulse here */ }}
                    onRemove={() => {
                        onRemoveContact(selectedDetailContact.id);
                        setSelectedDetailContact(null);
                    }}
                    onViewProfile={() => {
                        onViewProfile(selectedDetailContact.id);
                        setSelectedDetailContact(null);
                    }}
                />
            )}
        </div>
    );
};

export default ContactPage;
